import { PrimitiveAtom, atom } from 'jotai';

import { gracefullyCompressJson } from '../../../compression/stringCompression';
import { logger as baseLogger } from '../../../datadog/logger';
import { DataChannelUpdateStatus } from './constants';
import { DataChannelAppStateProperty, DataChannelMessage, WritableDataChannelAtom } from './types';

export async function formatDataChannelMessage(
  message: DataChannelMessage,
  maxMessageSizeBytes: number,
  { logger = baseLogger }: { logger?: typeof baseLogger } = {},
) {
  const [compressedJson, error] = await gracefullyCompressJson(message);

  if (error) {
    logger.error('Failed to compress datatrack message', {}, error);
    return null;
  }

  const messageSizeBytes = new TextEncoder().encode(compressedJson).length;
  const isMessageWithinSizeLimit = messageSizeBytes <= maxMessageSizeBytes;
  if (!isMessageWithinSizeLimit) {
    logger.error('DataChannel message too large', {
      messageSizeBytes,
      maxMessageSizeBytes,
    });
    return null;
  }

  return compressedJson;
}

export function getNewestDataChannelState<T>({
  receivedState,
  userState,
}: {
  receivedState: DataChannelAppStateProperty<T>;
  userState: DataChannelAppStateProperty<T>;
}): [DataChannelAppStateProperty<T>, DataChannelUpdateStatus] {
  if (receivedState.lastUpdated > userState.lastUpdated) {
    return [
      receivedState,
      receivedState.value === userState.value
        ? DataChannelUpdateStatus.LAST_UPDATED_CHANGED
        : DataChannelUpdateStatus.VALUE_AND_LAST_UPDATED_CHANGED,
    ];
  }
  return [userState, DataChannelUpdateStatus.NO_UPDATE_NEEDED];
}

/**
 *
 * @param baseAtom The parent atom of the data track property
 * @param key The key of the of the data track property
 * @returns A read/write atom with setter guards against updating the value with stale info
 */
export function createDataChannelPropertyAtom<
  Value, // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Key extends keyof any,
  Base extends Record<Key, DataChannelAppStateProperty<Value>>,
>(baseAtom: PrimitiveAtom<Base>, key: Key): WritableDataChannelAtom<Value> {
  return atom(
    (get) => get(baseAtom)[key],
    (get, set, update: DataChannelAppStateProperty<Value>) => {
      const current = get(baseAtom)[key];
      /**
       * If the current value is newer than the update,
       * we don't update the atom
       */
      const [_newest, comparisonResult] = getNewestDataChannelState({
        receivedState: update,
        userState: current,
      });
      if (comparisonResult === DataChannelUpdateStatus.NO_UPDATE_NEEDED) {
        return;
      }
      set(baseAtom, {
        ...get(baseAtom),
        [key]: { value: update.value, lastUpdated: update.lastUpdated },
      });
    },
  );
}
